<script lang="ts" setup>
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  useCredential,
  useCredentialCsr,
  useCompleteCredentialCsr,
  useRenameCredential,
  useRotateCredential,
  useRevokeCredential,
} from "@/queries/credentials";
import { useAlertStore } from "@/stores/alerts-store";
import { credentialKindLabel, credentialBadgeClass, credentialBadgeLabel, expiryClass, expiryLabel } from "../utils/credentialDisplay";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import PageHeader from "@/components/PageHeader.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import FormAlert from "@/components/FormAlert.vue";

const route = useRoute();
const router = useRouter();
const alertStore = useAlertStore();

const credentialId = computed(() => route.params.credentialId as string);
const { credential, credentialLoading: loading, credentialError: error } = useCredential(credentialId);

const isPendingCsr = computed(() => credential.value?.status === "PendingCsr");
const { csr, csrLoading } = useCredentialCsr(credentialId, isPendingCsr);

const { completeCsr, completeCsrPending, completeCsrError, completeCsrReset } = useCompleteCredentialCsr();
const { renameCredential, renameCredentialPending, renameCredentialError, renameCredentialReset } = useRenameCredential();
const { rotateCredential, rotateCredentialPending, rotateCredentialError, rotateCredentialReset } = useRotateCredential();
const { revokeCredential } = useRevokeCredential();

function fmt(d?: string | null): string {
  return d ? new Date(d).toLocaleString() : "—";
}

// ── CSR completion (PendingCsr only) ────────────────────────────────────────────────────────────

const certificatePem = ref("");

async function copyCsr() {
  await navigator.clipboard.writeText(csr.value);
  alertStore.addSuccess("Copied", "CSR copied to clipboard.");
}

function submitCertificate() {
  completeCsr(
    { id: credentialId.value, certificatePem: certificatePem.value.trim() },
    {
      onSuccess: () => {
        certificatePem.value = "";
        alertStore.addSuccess("Activated", "Certificate bound — the credential is ready to link.");
      },
    });
}

// ── Rename ──────────────────────────────────────────────────────────────────────────────────────

const showRename = ref(false);
const renameValue = ref("");

function openRename() {
  renameValue.value = credential.value?.name ?? "";
  renameCredentialReset();
  showRename.value = true;
}

function confirmRename() {
  renameCredential(
    { id: credentialId.value, name: renameValue.value.trim() },
    { onSuccess: () => { showRename.value = false; alertStore.addSuccess("Renamed", "Credential renamed."); } });
}

// ── Rotate — re-enter material of the same kind; linking connections pick it up on next use ─────

const showRotate = ref(false);
const rotate = ref({ pfxBase64: "", pfxFileName: "", password: "", privateKey: "", publicKey: "", passphrase: "" });

function openRotate() {
  rotate.value = { pfxBase64: "", pfxFileName: "", password: "", privateKey: "", publicKey: "", passphrase: "" };
  rotateCredentialReset();
  showRotate.value = true;
}

function onPfxFile(event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) { rotate.value.pfxBase64 = ""; rotate.value.pfxFileName = ""; return; }
  const reader = new FileReader();
  reader.onload = () => {
    rotate.value.pfxBase64 = String(reader.result).split(",")[1] ?? "";
    rotate.value.pfxFileName = file.name;
  };
  reader.readAsDataURL(file);
}

const canRotate = computed(() => {
  const r = rotate.value;
  if (credential.value?.kind === "X509") return !!r.pfxBase64;
  return !!r.privateKey.trim() && !!r.publicKey.trim();
});

function confirmRotate() {
  const r = rotate.value;
  const fields: Record<string, string> =
    credential.value?.kind === "X509"
      ? { pfx: r.pfxBase64, ...(r.password ? { password: r.password } : {}) }
      : { privateKey: r.privateKey, publicKey: r.publicKey.trim(), ...(r.passphrase ? { passphrase: r.passphrase } : {}) };
  rotateCredential(
    { id: credentialId.value, fields },
    { onSuccess: () => { showRotate.value = false; alertStore.addSuccess("Rotated", "Material updated — linking connections use it on next run."); } });
}

// ── Revoke ──────────────────────────────────────────────────────────────────────────────────────

function remove() {
  if (!credential.value) return;
  if (!confirm(`Revoke "${credential.value.name}"? It will be removed and can no longer be linked to connections.`)) return;
  revokeCredential(credential.value.id, {
    onSuccess: () => { alertStore.addSuccess("Revoked", "Credential revoked."); router.push("/connections/credentials"); },
    onError: (e) => alertStore.addError("Revoke failed", e.userMessage),
  });
}

function goBack() {
  router.push("/connections/credentials");
}
</script>

<template>
  <main>
    <PanelGrid :columns="1">
      <PanelCard>
        <template v-if="loading || error">
          <SkeletonLoader type="header" :error="error" />
        </template>

        <template v-else-if="credential">
          <PageHeader
            :title="credential.name"
            :breadcrumbs="[
              { label: 'Dashboard', href: '/' },
              { label: 'Connections', href: '/connections' },
              { label: 'Credentials', href: '/connections/credentials' },
              { label: credential.name },
            ]"
          >
            <template #actions>
              <button class="bb-btn-secondary-cta" @click="goBack"><i class="bi bi-arrow-left me-1"></i> Back</button>
              <button class="bb-btn-secondary-cta" @click="openRename"><i class="bi bi-pencil me-1"></i> Rename</button>
              <div class="dropdown">
                <button type="button" class="bb-btn-secondary-cta dropdown-toggle" title="More actions" data-bs-toggle="dropdown">
                  <i class="bi bi-three-dots"></i>
                </button>
                <ul class="dropdown-menu dropdown-menu-end">
                  <li v-if="credential.status === 'Active'">
                    <a class="dropdown-item" href="#" @click.prevent="openRotate"><i class="bi bi-arrow-repeat me-2"></i>Rotate material</a>
                  </li>
                  <li v-if="credential.status === 'Active'"><hr class="dropdown-divider" /></li>
                  <li><a class="dropdown-item text-danger" href="#" @click.prevent="remove"><i class="bi bi-trash me-2"></i>Revoke</a></li>
                </ul>
              </div>
            </template>
          </PageHeader>

          <!-- CSR completion — the credential is unusable until the signed certificate is bound. -->
          <div v-if="isPendingCsr" class="card border-warning mb-4">
            <div class="card-body">
              <h2 class="h5 d-flex align-items-center gap-2">
                <i class="bi bi-hourglass-split text-warning"></i> Awaiting signed certificate
              </h2>
              <p class="text-muted">
                Send the signing request below to your certificate authority, then paste the signed
                certificate (PEM) to activate this credential.
              </p>
              <div class="mb-3">
                <label class="form-label d-flex justify-content-between align-items-center" for="csr-text">
                  Certificate signing request
                  <button type="button" class="bb-btn-secondary-cta btn-sm" :disabled="csrLoading || !csr" @click="copyCsr">
                    <i class="bi bi-clipboard me-1"></i>Copy
                  </button>
                </label>
                <textarea id="csr-text" class="form-control font-monospace" rows="6" readonly :value="csrLoading ? 'Loading…' : csr"></textarea>
              </div>
              <form @submit.prevent="submitCertificate">
                <FormAlert :error="completeCsrError" @dismiss="completeCsrReset" />
                <div class="mb-3">
                  <label class="form-label" for="signed-certificate">Signed certificate (PEM)</label>
                  <textarea
                    id="signed-certificate" class="form-control font-monospace" rows="6" v-model="certificatePem"
                    placeholder="-----BEGIN CERTIFICATE-----"
                  ></textarea>
                </div>
                <button type="submit" class="bb-btn-primary-cta" :disabled="completeCsrPending || !certificatePem.trim()">
                  <span v-if="completeCsrPending" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  <i v-else class="bi bi-patch-check me-1"></i>Bind certificate
                </button>
              </form>
            </div>
          </div>

          <div class="row g-4">
            <!-- Details -->
            <div class="col-lg-6">
              <div class="card h-100">
                <div class="card-body">
                  <h2 class="h5 mb-3">Details</h2>
                  <dl class="row mb-0">
                    <dt class="col-sm-4">Status</dt>
                    <dd class="col-sm-8"><span class="badge" :class="credentialBadgeClass(credential.status)">{{ credentialBadgeLabel(credential.status) }}</span></dd>
                    <dt class="col-sm-4">Kind</dt>
                    <dd class="col-sm-8">{{ credentialKindLabel(credential.kind) }}</dd>

                    <template v-if="credential.kind === 'X509'">
                      <dt class="col-sm-4">Subject</dt>
                      <dd class="col-sm-8 font-monospace">{{ credential.metadata.subject }}</dd>
                      <dt class="col-sm-4">Issuer</dt>
                      <dd class="col-sm-8 font-monospace">{{ credential.metadata.issuer }}</dd>
                      <template v-if="credential.metadata.sans?.length">
                        <dt class="col-sm-4">SANs</dt>
                        <dd class="col-sm-8 font-monospace">{{ credential.metadata.sans.join(", ") }}</dd>
                      </template>
                      <dt class="col-sm-4">Valid from</dt>
                      <dd class="col-sm-8">{{ fmt(credential.metadata.notBefore) }}</dd>
                      <dt class="col-sm-4">Expires</dt>
                      <dd class="col-sm-8" :class="expiryClass(credential.metadata.notAfter)">{{ expiryLabel(credential.metadata.notAfter) }}</dd>
                      <dt class="col-sm-4">Thumbprint</dt>
                      <dd class="col-sm-8 font-monospace text-break">{{ credential.metadata.thumbprint }}</dd>
                    </template>

                    <template v-else>
                      <dt class="col-sm-4">Fingerprint</dt>
                      <dd class="col-sm-8 font-monospace text-break">{{ credential.metadata.fingerprint }}</dd>
                      <dt class="col-sm-4">Algorithm</dt>
                      <dd class="col-sm-8">{{ credential.metadata.algorithm }}</dd>
                      <template v-if="credential.metadata.comment">
                        <dt class="col-sm-4">Comment</dt>
                        <dd class="col-sm-8">{{ credential.metadata.comment }}</dd>
                      </template>
                      <template v-if="credential.metadata.userIds?.length">
                        <dt class="col-sm-4">User ids</dt>
                        <dd class="col-sm-8">{{ credential.metadata.userIds.join(", ") }}</dd>
                      </template>
                      <template v-if="credential.metadata.notAfter">
                        <dt class="col-sm-4">Expires</dt>
                        <dd class="col-sm-8" :class="expiryClass(credential.metadata.notAfter)">{{ expiryLabel(credential.metadata.notAfter) }}</dd>
                      </template>
                    </template>

                    <dt class="col-sm-4">Created</dt>
                    <dd class="col-sm-8">{{ fmt(credential.createdAt) }}</dd>
                    <template v-if="credential.rotatedAt">
                      <dt class="col-sm-4">Rotated</dt>
                      <dd class="col-sm-8">{{ fmt(credential.rotatedAt) }}</dd>
                    </template>
                  </dl>
                </div>
              </div>
            </div>

            <!-- Used by -->
            <div class="col-lg-6">
              <div class="card h-100">
                <div class="card-body">
                  <h2 class="h5 mb-3">Used by</h2>
                  <template v-if="credential.usedBy.length > 0">
                    <div class="list-group list-group-flush">
                      <router-link
                        v-for="usage in credential.usedBy"
                        :key="`${usage.connectionId}-${usage.role}`"
                        :to="`/connections/${usage.connectionId}`"
                        class="list-group-item list-group-item-action d-flex justify-content-between align-items-center px-0"
                      >
                        <span><i class="bi bi-link-45deg me-2 text-primary"></i>{{ usage.connectionName }}</span>
                        <span class="badge bg-secondary">{{ usage.role }}</span>
                      </router-link>
                    </div>
                  </template>
                  <p v-else class="text-muted mb-0">
                    Not linked to any connection. Link it from a connection's setup form.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </template>
      </PanelCard>
    </PanelGrid>

    <!-- Rename modal -->
    <div
      class="modal fade"
      :class="{ show: showRename }"
      :style="{ display: showRename ? 'block' : 'none' }"
      @click.self="showRename = false"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <form @submit.prevent="confirmRename">
            <div class="modal-header">
              <h2 class="modal-title">Rename credential</h2>
              <button type="button" class="btn-close" @click="showRename = false"></button>
            </div>
            <div class="modal-body">
              <FormAlert :error="renameCredentialError" @dismiss="renameCredentialReset" />
              <label class="form-label" for="rename-input">Name</label>
              <input id="rename-input" type="text" class="form-control" v-model="renameValue" required />
            </div>
            <div class="modal-footer">
              <button type="button" class="bb-btn-cancel" @click="showRename = false">Cancel</button>
              <button type="submit" class="bb-btn-primary-cta" :disabled="renameCredentialPending || !renameValue.trim()">
                <span v-if="renameCredentialPending" class="spinner-border spinner-border-sm me-2" role="status"></span>
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    <div v-if="showRename" class="modal-backdrop fade show"></div>

    <!-- Rotate modal — re-enter material of the same kind -->
    <div
      class="modal fade"
      :class="{ show: showRotate }"
      :style="{ display: showRotate ? 'block' : 'none' }"
      @click.self="showRotate = false"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <form @submit.prevent="confirmRotate">
            <div class="modal-header">
              <h2 class="modal-title">Rotate material</h2>
              <button type="button" class="btn-close" @click="showRotate = false"></button>
            </div>
            <div class="modal-body">
              <FormAlert :error="rotateCredentialError" @dismiss="rotateCredentialReset" />
              <p class="text-muted">
                Replace the {{ credentialKindLabel(credential?.kind ?? "X509") }} material. Every
                linking connection picks up the new material on its next use.
              </p>

              <template v-if="credential?.kind === 'X509'">
                <div class="mb-3">
                  <label class="form-label" for="rotate-pfx">PKCS#12 file (.pfx / .p12)</label>
                  <input id="rotate-pfx" type="file" class="form-control" accept=".pfx,.p12" @change="onPfxFile" />
                  <div v-if="rotate.pfxFileName" class="form-text">{{ rotate.pfxFileName }}</div>
                </div>
                <div class="mb-3">
                  <label class="form-label" for="rotate-password">Password <span class="text-muted">(if the file has one)</span></label>
                  <input id="rotate-password" type="password" class="form-control" v-model="rotate.password" autocomplete="off" />
                </div>
              </template>

              <template v-else>
                <div class="mb-3">
                  <label class="form-label" for="rotate-private-key">Private key</label>
                  <textarea
                    id="rotate-private-key" class="form-control font-monospace" rows="5" v-model="rotate.privateKey"
                    :placeholder="credential?.kind === 'SshKey' ? '-----BEGIN OPENSSH PRIVATE KEY-----' : '-----BEGIN PGP PRIVATE KEY BLOCK-----'"
                  ></textarea>
                </div>
                <div class="mb-3">
                  <label class="form-label" for="rotate-public-key">Public key</label>
                  <textarea
                    id="rotate-public-key" class="form-control font-monospace" :rows="credential?.kind === 'SshKey' ? 2 : 5" v-model="rotate.publicKey"
                    :placeholder="credential?.kind === 'SshKey' ? 'ssh-ed25519 AAAA... comment' : '-----BEGIN PGP PUBLIC KEY BLOCK-----'"
                  ></textarea>
                </div>
                <div class="mb-3">
                  <label class="form-label" for="rotate-passphrase">Passphrase <span class="text-muted">(if the private key has one)</span></label>
                  <input id="rotate-passphrase" type="password" class="form-control" v-model="rotate.passphrase" autocomplete="off" />
                </div>
              </template>
            </div>
            <div class="modal-footer">
              <button type="button" class="bb-btn-cancel" @click="showRotate = false">Cancel</button>
              <button type="submit" class="bb-btn-primary-cta" :disabled="rotateCredentialPending || !canRotate">
                <span v-if="rotateCredentialPending" class="spinner-border spinner-border-sm me-2" role="status"></span>
                <i v-else class="bi bi-arrow-repeat me-1"></i>Rotate
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    <div v-if="showRotate" class="modal-backdrop fade show"></div>
  </main>
</template>
